import React, { useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CallIcon from "@mui/icons-material/Call";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarIcon from "@mui/icons-material/Star";
import { config } from "../../../../../config/config";
import { apiList, invokeApi } from "../../../../../apis/apiServices";
import mobileNoValidation from "../../../../../common/common";
import "./PainterHeroBanner.css";

const PainterHeroBanner = ({ content }) => {
  const [cookies] = useCookies();
  const { userData } = useSelector((state) => state.user);
  const { locationData } = useSelector((state) => state.location);
  const zoneMappingData = locationData?.countries;

  const [formData, setFormData] = useState({
    email: userData?.user?.email || "",
    customerName: userData?.user?.fullName || "",
    mobileNumber: userData?.user?.mobileNumber || "",
    leadSource: content.leadSource,
    leadType: "Own",
    customerDistrict: userData?.user?.district || "",
    customerState: userData?.user?.state || "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mobileValidate = mobileNoValidation(formData.mobileNumber);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerName.trim()) newErrors.customerName = "Full name is required.";
    if (!mobileValidate) newErrors.mobileNumber = "A valid mobile number is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const notify = (type, message) => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      transition: Bounce,
    });
  };

  const addLead = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const response = await invokeApi(config.apiDomains + apiList.addLead, formData, cookies);
      if (response && (response.status === "200" || response.data.responseMessage === "Successful")) {
        notify("success", "Thank you! Our painting expert will call you shortly.");
        setFormData({
          email: "",
          customerName: "",
          mobileNumber: "",
          leadSource: content.leadSource,
          leadType: "Own",
          customerDistrict: "",
          customerState: "",
        });
      } else {
        notify("error", "Sorry, something went wrong. Please try again.");
      }
    } catch (error) {
      notify("error", "An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const stats = [
    { value: "12+", label: "Years Experience" },
    { value: "9,000+", label: "Homes Painted" },
    { value: "4.8/5", label: "Average Rating" },
  ];

  return (
    <section className="pnt-hero" aria-label="Professional painting services banner">
      <div className="pnt-hero-blob pnt-hero-blob-a" />
      <div className="pnt-hero-blob pnt-hero-blob-b" />

      <div className="pnt-hero-inner">
        <div className="pnt-hero-grid">
          {/* Left column */}
          <div className="pnt-hero-copy">
            <span className="pnt-hero-eyebrow">Get My Housing Painting Experts</span>
            <h1 className="pnt-hero-title">{content.heroSection.heroTitle}</h1>
            <p className="pnt-hero-desc">{content.heroSection.heroTitleDes}</p>

            <ul className="pnt-hero-feature-list">
              {content.heroSection.heroList.map((item, index) => (
                <li key={index}>
                  <CheckCircleIcon className="pnt-hero-feature-icon" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="pnt-hero-actions">
              <a href="#painter-quote-form" className="pnt-btn-primary">
                {content.heroSection.heroButton}
              </a>
              <a href={"tel:" + config.mobileNumber1} className="pnt-btn-ghost">
                <CallIcon fontSize="small" />
                Talk to an Expert
              </a>
            </div>

            <div className="pnt-hero-stats">
              {stats.map((stat, index) => (
                <div className="pnt-hero-stat" key={index}>
                  <span className="pnt-hero-stat-value">{stat.value}</span>
                  <span className="pnt-hero-stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div className="pnt-hero-visual">
            <div className="pnt-hero-frame">
              <img
                src={content.heroSection.bannerImage}
                alt="Professional painter at work"
                className="pnt-hero-img"
                loading="lazy"
              />
              <div className="pnt-hero-badge pnt-hero-badge-top">
                <VerifiedIcon className="pnt-hero-badge-icon" />
                <div>
                  <strong>Verified Painters</strong>
                  <span>Trained &amp; background-checked</span>
                </div>
              </div>
              <div className="pnt-hero-badge pnt-hero-badge-bottom">
                <StarIcon className="pnt-hero-badge-icon" />
                <div>
                  <strong>4.8 Customer Rating</strong>
                  <span>Based on 2,400+ reviews</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating quote form card */}
        <div className="pnt-hero-form-card" id="painter-quote-form">
          <div className="pnt-hero-form-head">
            <h2>{content.heroSection.formTitle}</h2>
            <p>Free site visit &middot; No obligation quote &middot; Response within 24 hours</p>
          </div>
          <form className="pnt-hero-form" onSubmit={addLead}>
            <div className="pnt-form-row">
              <div className="pnt-input-container">
                <input
                  placeholder="Full Name *"
                  autoComplete="name"
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  aria-label="Enter your full name"
                />
                {errors.customerName && <p className="pnt-error">{errors.customerName}</p>}
              </div>
              <div className="pnt-input-container">
                <input
                  placeholder="Phone Number *"
                  autoComplete="tel"
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  aria-label="Enter your phone number"
                />
                {errors.mobileNumber && <p className="pnt-error">{errors.mobileNumber}</p>}
              </div>
              <div className="pnt-input-container">
                <input
                  placeholder="Email Address"
                  type="email"
                  autoComplete="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  aria-label="Enter your email"
                />
              </div>
              <div className="pnt-input-container">
                <select
                  value={formData.customerState}
                  name="customerState"
                  aria-label="Select your state"
                  onChange={handleChange}
                >
                  <option value="">Select State</option>
                  {zoneMappingData
                    ?.find((item) => item.countryName === "India")
                    ?.states.map((item, index) => (
                      <option key={index} value={item.stateName}>
                        {item.stateName}
                      </option>
                    ))}
                </select>
              </div>
              <div className="pnt-input-container">
                <select
                  aria-label="Select your city"
                  name="customerDistrict"
                  onChange={handleChange}
                  value={formData.customerDistrict}
                  disabled={!formData.customerState}
                >
                  <option value="">Select City</option>
                  {zoneMappingData
                    ?.find((item) => item.countryName === "India")
                    ?.states?.find((state) => state.stateName === formData.customerState)
                    ?.districts?.map((district, index) => (
                      <option key={index} value={district.districtName}>
                        {district.districtName}
                      </option>
                    ))}
                </select>
              </div>
              <button className="pnt-form-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Get Free Quote"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PainterHeroBanner;
